'use client';

/* eslint-disable react-perf/jsx-no-new-function-as-prop -- scroll buttons */
/* eslint-disable react-perf/jsx-no-new-array-as-prop -- static rail props */

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import CoverOverlay from '../../components/cover/CoverOverlay';
import Button from '../../components/ui/Button';
import useToastMessage from '@/hooks/useToastMessage';
import { truncatePlainText, formatBlogPublishShort, resolveBlogCoverSrc } from '@/lib/blogContent';
import { BLOG_LIST_EXCERPT_MAX_LENGTH } from '@/lib/blogConstants';
import { startDesignFromDestinationSlug } from '@/utils/startDesignFromDestination';

import styles from '../../../styles/pages/blog/blog.module.scss';

/** Хоризонтален gap между слайдове (съвпада с SCSS gap на .articleRailScroller). */
const RAIL_GAP_PX = 14;

const LAZY_RAIL_ROOT_MARGIN = '200px';
const LAZY_RAIL_MIN_HEIGHT_PX = 280;
const LAZY_RAIL_PLACEHOLDER_STYLE = { minHeight: LAZY_RAIL_MIN_HEIGHT_PX };
const POST_RAIL_EAGER_IMAGE_COUNT = 2;

/** Корици */
const COVER_SLIDE_MIN_PX = 180;
const COVER_SLIDE_MAX_PX = 300;
const COVER_RAIL_MIN_VIEWPORT_FOR_FIVE_COLS_PX = 800;
const COVER_DESKTOP_COLUMN_COUNT = 5;
const COVER_SLIDE_MIN_WHEN_FIVE_COLUMNS_PX = 136;

/** Постове „Прочетете още“ */
const POST_CARD_MIN_PX = 240;
const POST_CARD_MAX_PX = 460;
const POST_RAIL_MIN_VIEWPORT_FOR_THREE_COLS_PX = 620;
const POST_DESKTOP_COLUMN_COUNT = 3;
const POST_SLIDE_MIN_WHEN_THREE_COLUMNS_PX = 175;

function computeCoverSlideWidthPx(viewportWidth) {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) {
    return COVER_SLIDE_MIN_PX;
  }
  const gap = RAIL_GAP_PX;
  const minW = COVER_SLIDE_MIN_PX;
  const maxW = COVER_SLIDE_MAX_PX;

  if (viewportWidth >= COVER_RAIL_MIN_VIEWPORT_FOR_FIVE_COLS_PX) {
    const k = COVER_DESKTOP_COLUMN_COUNT;
    const raw = (viewportWidth - (k - 1) * gap) / k;
    if (raw >= COVER_SLIDE_MIN_WHEN_FIVE_COLUMNS_PX) {
      return Math.round(Math.min(maxW, raw) * 100) / 100;
    }
  }

  let k = Math.max(1, Math.floor((viewportWidth + gap) / (minW + gap)));
  let width = (viewportWidth - (k - 1) * gap) / k;
  while (width < minW && k > 1) {
    k -= 1;
    width = (viewportWidth - (k - 1) * gap) / k;
  }
  while (width > maxW && k > 1) {
    k -= 1;
    width = (viewportWidth - (k - 1) * gap) / k;
  }
  width = Math.min(maxW, width);
  if (width < minW) {
    return Math.round(Math.min(maxW, viewportWidth) * 100) / 100;
  }
  return Math.round(Math.max(minW, width) * 100) / 100;
}

function computePostSlideWidthPx(viewportWidth) {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) {
    return POST_CARD_MIN_PX;
  }
  const gap = RAIL_GAP_PX;
  const minW = POST_CARD_MIN_PX;
  const maxW = POST_CARD_MAX_PX;

  if (viewportWidth >= POST_RAIL_MIN_VIEWPORT_FOR_THREE_COLS_PX) {
    const k = POST_DESKTOP_COLUMN_COUNT;
    const raw = (viewportWidth - (k - 1) * gap) / k;
    if (raw >= POST_SLIDE_MIN_WHEN_THREE_COLUMNS_PX) {
      return Math.round(Math.min(maxW, raw) * 100) / 100;
    }
  }

  let k = Math.max(1, Math.floor((viewportWidth + gap) / (minW + gap)));
  let width = (viewportWidth - (k - 1) * gap) / k;
  while (width < minW && k > 1) {
    k -= 1;
    width = (viewportWidth - (k - 1) * gap) / k;
  }
  while (width > maxW && k > 1) {
    k -= 1;
    width = (viewportWidth - (k - 1) * gap) / k;
  }
  width = Math.min(maxW, width);
  if (width < minW) {
    return Math.round(Math.min(maxW, viewportWidth) * 100) / 100;
  }
  return Math.round(Math.max(minW, width) * 100) / 100;
}

function snapAlignedDomIndex(el) {
  const slides = el.querySelectorAll('[data-carousel-slide]');
  const n = slides.length;
  if (n <= 1) {
    return 0;
  }
  const elRect = el.getBoundingClientRect();
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const d = Math.abs(slides[i].getBoundingClientRect().left - elRect.left);
    if (d < bestDist || (d === bestDist && i < best)) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function scrollSlideToDom(el, domIdx, behavior) {
  const slides = el.querySelectorAll('[data-carousel-slide]');
  const node = slides[domIdx];
  if (!node) {
    return;
  }
  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
  const elRect = el.getBoundingClientRect();
  const nodeRect = node.getBoundingClientRect();
  const targetLeft = el.scrollLeft + (nodeRect.left - elRect.left);
  const left = Math.min(Math.max(0, Math.round(targetLeft)), Math.round(maxScroll));
  el.scrollTo({
    left,
    behavior: behavior === 'smooth' ? 'smooth' : 'auto',
  });
}

/** Монтира съдържанието на релса при приближаване до viewport (lazy load). */
function LazyRailMount({ children }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) {
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: LAZY_RAIL_ROOT_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return (
    <div ref={ref} style={inView ? undefined : LAZY_RAIL_PLACEHOLDER_STYLE}>
      {inView ? children : null}
    </div>
  );
}

/**
 * @param {{
 *   card: { slug: string; destinationName?: string };
 *   templateIndexForStart: number;
 *   imageLoading?: string;
 * }} props
 */
function BlogCoverRailItem({ card, templateIndexForStart, imageLoading = 'lazy' }) {
  const router = useRouter();
  const showToast = useToastMessage();
  const [isStarting, setIsStarting] = useState(false);
  const startInFlightRef = useRef(false);

  const handleStartDesign = useCallback(() => {
    if (startInFlightRef.current) {
      return;
    }
    startInFlightRef.current = true;
    setIsStarting(true);
    startDesignFromDestinationSlug({
      slug: card.slug,
      templateIndex: templateIndexForStart,
      destinationName: card.destinationName ?? card.slug,
      router,
    }).catch((err) => {
      console.error('Failed to create draft:', err);
      showToast({
        msg: 'Неуспешно стартиране на дизайна. Моля, опитай отново.',
        severity: 'error',
        icon: true,
        position: 'bottomRight',
      });
      setIsStarting(false);
      startInFlightRef.current = false;
    });
  }, [card.slug, card.destinationName, templateIndexForStart, router, showToast]);

  const overlay = card.template ? (
    <CoverOverlay
      template={card.template}
      destinationName={card.destinationName}
      size="md"
      imageLoading={imageLoading}
      showCatalogBadges
      showFavoriteButton={false}
      className={styles.coverRecoOverlayFill}
    />
  ) : (
    <CoverOverlay
      template={null}
      destinationName={card.destinationName}
      size="md"
      imageLoading={imageLoading}
      placeholderChar={card.destinationName?.charAt(0) ?? '?'}
      showFavoriteButton={false}
      className={styles.coverRecoOverlayFill}
    />
  );

  return (
    <div className={styles.coverRecoCard}>
      <div className={styles.coverRecoCoverSlot}>
        {overlay}
        <div className={styles.coverRecoHover}>
          <div className={styles.coverRecoButtonSlot}>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={handleStartDesign}
              disabled={isStarting}
            >
              {isStarting ? 'Подготвяне…' : 'Започни дизайн'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ variant: 'posts' | 'destinations'; title: string; cards: object[] }} props
 */
function InnerRail({ variant, title, cards }) {
  const scrollerRef = useRef(null);
  const viewportRef = useRef(null);
  const [slideWidthPx, setSlideWidthPx] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const railFingerprint = useMemo(
    () =>
      cards
        .map((c) => (variant === 'destinations' ? `${c.slug}:${c.templateId ?? ''}` : c.slug))
        .join('\0'),
    [cards, variant],
  );

  const syncScrollArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || cards.length <= 1) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    const scrollW = el.scrollWidth;
    const clientW = el.clientWidth;
    const maxScroll = Math.max(0, scrollW - clientW);
    if (maxScroll <= 2) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    const sl = el.scrollLeft;
    const edgeSlack = 12;
    setCanScrollPrev(sl > edgeSlack);
    setCanScrollNext(sl + clientW < scrollW - edgeSlack);
  }, [cards.length]);

  const scrollByOne = useCallback(
    (dir) => {
      const el = scrollerRef.current;
      if (!el || cards.length <= 1) {
        return;
      }
      const n = cards.length;
      const domIdx = snapAlignedDomIndex(el);
      const next = domIdx + dir;
      if (next < 0 || next >= n) {
        return;
      }
      scrollSlideToDom(el, next, 'smooth');
    },
    [cards.length],
  );

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el || cards.length === 0) {
      setSlideWidthPx(null);
      return undefined;
    }
    if (variant === 'posts' && cards.length === 1) {
      setSlideWidthPx(null);
      return undefined;
    }
    const measure = () => {
      const w = el.clientWidth;
      if (variant === 'destinations') {
        setSlideWidthPx(computeCoverSlideWidthPx(w));
      } else {
        setSlideWidthPx(computePostSlideWidthPx(w));
      }
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [variant, railFingerprint, cards.length]);

  /* Нулирай скрол при смяна на данните (не при slideWidthPx). */
  useEffect(() => {
    const el = scrollerRef.current;
    if (el && cards.length > 1) {
      el.scrollLeft = 0;
    }
    const id = requestAnimationFrame(() => {
      syncScrollArrows();
    });
    return () => cancelAnimationFrame(id);
  }, [railFingerprint, cards.length, syncScrollArrows]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      syncScrollArrows();
    });
    return () => cancelAnimationFrame(id);
  }, [slideWidthPx, syncScrollArrows]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || cards.length <= 1) {
      return undefined;
    }
    const onScroll = () => syncScrollArrows();
    const onScrollEnd = () => syncScrollArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', onScrollEnd);
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            syncScrollArrows();
          })
        : null;
    ro?.observe(el);
    syncScrollArrows();
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', onScrollEnd);
      ro?.disconnect();
    };
  }, [cards.length, slideWidthPx, railFingerprint, syncScrollArrows]);

  if (!cards.length) {
    return null;
  }

  const isDestinations = variant === 'destinations';
  const postsMulti = variant === 'posts' && cards.length > 1;
  const postsSingle = variant === 'posts' && cards.length === 1;
  const showRailChrome = cards.length > 1;

  let slideClassName = styles.carouselSlideCover;
  if (variant === 'posts') {
    if (postsSingle) {
      slideClassName = styles.carouselSlidePostSingle;
    } else {
      slideClassName = styles.carouselSlidePost;
    }
  }

  const scrollerClassNames = [
    styles.articleRailScroller,
    postsSingle ? styles.articleRailScrollerPostsSingle : '',
  ]
    .filter(Boolean)
    .join(' ');

  const railCssVars =
    isDestinations && slideWidthPx != null
      ? { '--blog-cover-slide-px': `${slideWidthPx}px` }
      : postsMulti && slideWidthPx != null
        ? { '--blog-post-slide-px': `${slideWidthPx}px` }
        : undefined;

  const railScroller = (
    <div ref={viewportRef} className={styles.articleRailViewport}>
      <div ref={scrollerRef} className={scrollerClassNames} style={railCssVars}>
        {variant === 'posts'
          ? cards.map((card, index) => {
              const excerpt = card.excerpt?.trim()
                ? truncatePlainText(card.excerpt, BLOG_LIST_EXCERPT_MAX_LENGTH)
                : truncatePlainText(card.contentPreview || '', BLOG_LIST_EXCERPT_MAX_LENGTH);
              const imageLoading = index < POST_RAIL_EAGER_IMAGE_COUNT ? 'eager' : 'lazy';
              return (
                <div key={card.slug} className={slideClassName} data-carousel-slide>
                  <Link href={`/blog/${card.slug}`} className={styles.articleRecoCard}>
                    <div className={styles.articleRecoImageWrap}>
                      <Image
                        src={resolveBlogCoverSrc(card.coverImageUrl)}
                        alt={card.title}
                        fill
                        className={styles.carouselImage}
                        sizes="(max-width: 640px) 92vw, 460px"
                        loading={imageLoading}
                      />
                    </div>
                    <div className={styles.articleRecoMetaRow}>
                      <span className={styles.articleRecoMetaLeft}>
                        <span className={styles.articleRecoMetaLabel}>Категория:</span>{' '}
                        {card.categoryName || '—'}
                      </span>
                      <span className={styles.articleRecoMetaRight}>
                        <span className={styles.articleRecoMetaLabel}>Дата:</span>{' '}
                        {formatBlogPublishShort(card.publishedAt)}
                      </span>
                    </div>
                    <h3 className={styles.articleRecoCardTitle}>{card.title}</h3>
                    <p className={styles.articleRecoExcerpt}>{excerpt}</p>
                    <span className={styles.articleRecoCta}>Прочети повече</span>
                  </Link>
                </div>
              );
            })
          : cards.map((card, index) => {
              const templateIndexForStart =
                card.templateIndex != null && Number.isFinite(Number(card.templateIndex))
                  ? Number(card.templateIndex)
                  : 0;
              const slideKey = `${card.slug}-${card.templateId ?? 'none'}`;
              const imageLoading = index < POST_RAIL_EAGER_IMAGE_COUNT ? 'eager' : 'lazy';
              return (
                <div key={slideKey} className={slideClassName} data-carousel-slide>
                  <BlogCoverRailItem
                    card={card}
                    templateIndexForStart={templateIndexForStart}
                    imageLoading={imageLoading}
                  />
                </div>
              );
            })}
      </div>
    </div>
  );

  const prevArrowClass = `${styles.articleRailHomeArrow} ${!canScrollPrev ? styles.articleRailHomeArrowHidden : ''}`;
  const nextArrowClass = `${styles.articleRailHomeArrow} ${!canScrollNext ? styles.articleRailHomeArrowHidden : ''}`;

  return (
    <section
      className={`${styles.articleRail} ${isDestinations ? styles.articleRailWideBleed : ''}`}
      aria-labelledby={`rail-${variant}`}
    >
      <h2 id={`rail-${variant}`} className={styles.articleRailTitle}>
        {title}
      </h2>
      {showRailChrome ? (
        <div className={styles.articleRailRowSpaced}>
          <button
            type="button"
            className={prevArrowClass}
            onClick={() => scrollByOne(-1)}
            disabled={!canScrollPrev}
            aria-label="Назад"
          >
            <svg viewBox="0 0 24 24" className={styles.articleRailHomeArrowIcon} aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className={styles.articleRailMid}>{railScroller}</div>
          <button
            type="button"
            className={nextArrowClass}
            onClick={() => scrollByOne(1)}
            disabled={!canScrollNext}
            aria-label="Напред"
          >
            <svg viewBox="0 0 24 24" className={styles.articleRailHomeArrowIcon} aria-hidden>
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      ) : (
        <div className={styles.articleRailSingleWrap}>{railScroller}</div>
      )}
    </section>
  );
}

export default function BlogArticleRails({ postCards = [], destinationCards = [] }) {
  return (
    <div className={styles.articleRailsWrap}>
      {postCards.length > 0 ? (
        <LazyRailMount>
          <InnerRail variant="posts" title="Прочетете още" cards={postCards} />
        </LazyRailMount>
      ) : null}
      {destinationCards.length > 0 ? (
        <LazyRailMount>
          <InnerRail variant="destinations" title="Препоръчани корици" cards={destinationCards} />
        </LazyRailMount>
      ) : null}
    </div>
  );
}
